import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { Trash2 } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { venuesUpTarget } from '@/auth/access'
import { isInVenueSupportMode } from '@/auth/roles'
import { useDeleteVenue } from '@/hooks/useVenues'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { VenueDto } from '@lib/dto/venue.dto'

/**
 * What deleting a venue actually does, in the words of someone it happens to.
 *
 * The old copy said the venue would "leave lists and pickers" and that connected
 * events "keep their history" — the first is the vocabulary of the filter that
 * implements it, and the second reads as a safety net. Neither told an admin the
 * one thing that matters most: there is no way back. Deletion is a soft delete so
 * event rows keep a valid foreign key, but nothing in the app can undo it (see
 * `VenueRepository.findDeletedByPrismmRef`), and a venue with no Prismm ref
 * cannot even be revived by re-importing. Until there is a restore, the copy has
 * to read as final, because it is.
 */
function deletionCopy(name: string): string {
  return (
    `Delete “${name}”. This cannot be undone: a deleted venue cannot be restored. ` +
    'It leaves the venues list, the venue switcher and every venue dropdown, and nothing ' +
    'can be assigned to it again — no events, furniture or layouts. Events already booked ' +
    'here keep their plans but are stranded at a venue that no longer exists.'
  )
}

/**
 * Org-admin danger-zone action: delete (soft-delete) a venue. Owns confirm
 * state, dialog, and mutation so settings stays pure composition.
 */
export function VenueDeleteAction({ venue }: { venue: VenueDto }): React.ReactElement {
  const navigate = useNavigate()
  const { user, exitVenue } = useAuth()
  const upTarget = venuesUpTarget(user)
  const insideThisVenue = isInVenueSupportMode(user) && user.supportVenue.venueId === venue.id
  const deleteVenue = useDeleteVenue()
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  // Deleted, and still inside it because leaving failed. Not offered again: the
  // venue is gone, and a second delete could only fail.
  const [deleted, setDeleted] = React.useState(false)
  const [leaving, setLeaving] = React.useState(false)

  function close(): void {
    setConfirmOpen(false)
    setError(null)
  }

  /**
   * Archiving the venue you are working in has to end the overlay too.
   * Otherwise the session stays pointed at a venue that no longer exists: the
   * switcher keeps naming it, the sidebar Settings entry still links to it, and
   * every list comes back empty.
   */
  async function leave(): Promise<void> {
    setError(null)
    setLeaving(true)
    try {
      await exitVenue()
    } catch (err) {
      setError(requestErrorMessage(err))
      return
    } finally {
      setLeaving(false)
    }
    close()
    void navigate('/venues/all', { replace: true })
  }

  function confirm(): void {
    setError(null)
    deleteVenue.mutate(
      { id: venue.id },
      {
        onSuccess: async () => {
          if (insideThisVenue) {
            setDeleted(true)
            await leave()
            return
          }
          close()
          void navigate(upTarget.to)
        },
        onError: (err) => setError(requestErrorMessage(err)),
      },
    )
  }

  return (
    <Card className="flex flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 space-y-1">
        <p className="text-sm font-medium">Delete this venue</p>
        <p className="text-sm text-ink-muted">
          {deletionCopy(venue.name)}
        </p>
      </div>
      {/* Outlined in danger ink, as "Delete event" is; the filled red button is
          the one in the dialog that confirms. */}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="shrink-0 text-danger-ink hover:bg-danger-soft hover:text-danger-ink"
        onClick={() => {
          setError(null)
          setConfirmOpen(true)
        }}
      >
        <Trash2 className="h-4 w-4" />
        Delete venue
      </Button>

      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open) close()
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{deleted ? 'Venue deleted' : 'Delete venue?'}</DialogTitle>
            <DialogDescription>
              {deleted
                ? `“${venue.name}” is deleted, but you are still working inside it. Leave it to go back to all venues.`
                : deletionCopy(venue.name)}
            </DialogDescription>
          </DialogHeader>
          {error ? <FormError>{error}</FormError> : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>
              {deleted ? 'Close' : 'Cancel'}
            </Button>
            {deleted ? (
              <Button pending={leaving} type="button" onClick={() => void leave()}>
                Leave the venue
              </Button>
            ) : (
              <Button pending={deleteVenue.isPending} type="button" variant="destructive" onClick={confirm}>
                <Trash2 className="h-4 w-4" />
                {deleteVenue.isPending ? 'Deleting…' : 'Delete venue'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
